import ChevronRight from '@/assets/icons/ChevronRight.svg';
import { useTrackPromotion } from '@/hooks/useTrackPromotion';
import type { Ga4Promotion } from '@/types';
import { buildCloudflareImageUrl } from '@/utils/buildCloudflareImageUrl';
import { slugify } from '@/utils/slugify';
import { useRef } from 'react';
import type { InterruptionItem } from './types';

interface InterruptionTileProps {
  data: InterruptionItem;
  layout?: 'grid' | 'list';
}

export const InterruptionTile = ({
  data,
  layout = 'grid',
}: InterruptionTileProps) => {
  const { variant, eyebrow, message, subText, image, cta, link, ctaColour } =
    data;
  const href = cta?.url ?? link ?? undefined;

  const ref = useRef<HTMLAnchorElement & HTMLDivElement>(null);
  const promotion: Ga4Promotion | null = message
    ? {
        promotion_id: slugify(message) || 'interruption-tile',
        promotion_name: message,
        creative_name: eyebrow ?? message,
        creative_slot: 'interruption-tile',
      }
    : null;
  const { onSelect } = useTrackPromotion(ref, promotion ? [promotion] : []);
  const imageSrc = buildCloudflareImageUrl(image?.mainSrc ?? '', {
    width: 460,
  });
  const isOverlay = variant === 'overlay';

  const className = [
    'ku01',
    isOverlay ? 'ku01--overlay' : 'ku01--plinth',
    layout === 'list' ? 'ku01--list' : '',
    imageSrc ? 'ku01--has-image' : '',
    ctaColour ?? 'cta-black',
  ]
    .filter(Boolean)
    .join(' ');

  // V1 — text laid over a full-bleed image (or a brand-dark panel when no image).
  const overlayInner = (
    <div
      className={`ku01-overlay${imageSrc ? '' : ' ku01-overlay--empty'}`}
      style={imageSrc ? { backgroundImage: `url(${imageSrc})` } : undefined}
      role={imageSrc ? 'img' : undefined}
      aria-label={imageSrc ? (image?.altText ?? '') : undefined}
    >
      <div className="ku01-overlay-content">
        <div className="ku01-meta">
          {eyebrow ? <span className="ku01-eyebrow">{eyebrow}</span> : null}
          <span className="ku01-title">{message}</span>
          {subText ? <span className="ku01-sub">{subText}</span> : null}
        </div>
        {cta ? (
          <span className="ku01-link">
            {cta.text}
            <ChevronRight />
          </span>
        ) : null}
      </div>
    </div>
  );

  // V2 — image plinth on top, eyebrow/title/sub below (mirrors the product tile).
  const plinthInner = (
    <>
      <div className={`ku01-plinth${imageSrc ? '' : ' ku01-plinth--empty'}`}>
        {imageSrc ? (
          <img
            alt={image?.altText ?? ''}
            className="ku01-img"
            decoding="async"
            loading="lazy"
            src={imageSrc}
          />
        ) : null}
      </div>
      <div className="ku01-content">
        <div className="ku01-meta">
          {eyebrow ? <span className="ku01-eyebrow">{eyebrow}</span> : null}
          <span className="ku01-title">{message}</span>
          {subText ? <span className="ku01-sub">{subText}</span> : null}
        </div>
        {cta ? (
          <span className="ku01-cta">
            {cta.text}
            <ChevronRight />
          </span>
        ) : null}
      </div>
    </>
  );

  const inner = isOverlay ? overlayInner : plinthInner;

  return href ? (
    <a
      aria-label={message}
      className={className}
      href={href}
      onClick={() => promotion && onSelect(promotion)}
      ref={ref}
    >
      {inner}
    </a>
  ) : (
    <div className={className} ref={ref}>
      {inner}
    </div>
  );
};
